import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import { GET_ACADEMY_PROGRESS } from '../graphql/queries';
import { SAVE_ACADEMY_PROGRESS } from '../graphql/mutations';

/**
 * The Central de Aprendizado as trails: each subject's videos, watched in order, with what the member has seen.
 * The progress lives on their account (the API's academyProgress), so it follows them between phone and desktop;
 * a video counts as watched from 90% in, or when marked.
 */

const WATCHED_SHARE = 0.9;
// Under this much in, a video counts as not started
const STARTED_AFTER = 20;

/** 14:41, or 1:02:05 */
export const clock = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
};

/** "14 min", "1 h 12 min", "12 h" */
export const minutesLabel = (seconds) => {
  const minutes = Math.max(1, Math.round((seconds || 0) / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest && hours < 10 ? `${hours} h ${rest} min` : `${hours} h`;
};

/** The subjects with videos in the site's language, each video knowing its place in the trail. */
export const trailsFrom = (categories, language) =>
  (categories || [])
    .map(({ id, attributes: { namePt, nameEn, slug, videos } }) => ({
      id,
      slug,
      name: language === 'pt' ? namePt : nameEn,
      videos: ((language === 'pt' ? videos?.pt : videos?.en) || []).map((video, index) => ({ ...video, index })),
    }))
    .filter((trail) => trail.videos.length > 0);

export const isWatched = (progress, video) => !!progress?.[video.youtubeId]?.done;

/** How much of a video has been seen, 0 to 1. */
export const watchedShare = (progress, video) => {
  const entry = progress?.[video.youtubeId];
  if (!entry) return 0;
  if (entry.done) return 1;
  const duration = entry.d || video.durationSec;
  return duration ? Math.min(1, entry.t / duration) : 0;
};

/** Seconds left in a video started and not finished, or null. */
export const secondsLeft = (progress, video) => {
  const entry = progress?.[video.youtubeId];
  if (!entry || entry.done || entry.t <= STARTED_AFTER) return null;
  return Math.max(0, (entry.d || video.durationSec || 0) - entry.t);
};

/** Where to pick a video up: a little before where it stopped, or the start. */
export const resumeAt = (progress, video) => (secondsLeft(progress, video) === null ? 0 : Math.max(0, progress[video.youtubeId].t - 3));

export const watchedCount = (trail, progress) => trail.videos.filter((video) => isWatched(progress, video)).length;

/**
 * The lesson to go on with: the one started most recently and not finished, else the first not yet watched (the
 * first, once all are).
 */
export const nextLessonIndex = (trail, progress) => {
  const started = trail.videos
    .filter((video) => secondsLeft(progress, video) !== null)
    .sort((a, b) => progress[b.youtubeId].at - progress[a.youtubeId].at)[0];
  if (started) return started.index;
  const index = trail.videos.findIndex((video) => !isWatched(progress, video));
  return index === -1 ? 0 : index;
};

export const trailLength = (trail) => trail.videos.reduce((total, video) => total + (video.durationSec || 0), 0);

export const channelCount = (trail) => new Set(trail.videos.map((video) => video.channel)).size;

/** The trail of the video seen most recently, or the first trail. */
export const lastTrail = (trails, progress) => {
  let latest = null;
  trails.forEach((trail) =>
    trail.videos.forEach((video) => {
      const at = progress?.[video.youtubeId]?.at;
      if (at && (!latest || at > latest.at)) latest = { at, trail };
    })
  );
  return latest?.trail || trails[0] || null;
};

/** The member's progress, and saving one video's (shown at once; the API keeps it). */
export const useAcademyProgress = ({ skip = false } = {}) => {
  const { data } = useQuery(GET_ACADEMY_PROGRESS, { skip });
  const [saveProgress] = useMutation(SAVE_ACADEMY_PROGRESS);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    if (data?.academyProgress) setProgress(data.academyProgress);
  }, [data]);

  const save = useCallback(
    (youtubeId, update) => {
      setProgress((previous) => {
        const entry = previous[youtubeId] || { t: 0, d: 0, done: false };
        const t = update.position ?? entry.t;
        const d = update.duration ?? entry.d;
        const done = typeof update.done === 'boolean' ? update.done : entry.done || (d > 0 && t / d >= WATCHED_SHARE);
        return { ...previous, [youtubeId]: { t, d, done, at: Date.now() } };
      });
      // A save that fails stays on screen; the next one carries the latest position anyway
      saveProgress({ variables: { youtubeId, ...update } }).catch(() => {});
    },
    [saveProgress]
  );

  return { progress, save };
};
